import type { JSX } from 'react';

import type { BuilderStore } from '../state.js';

import { SelectField, TextField } from './Field.js';

export interface ProfileBasicsEditorProps {
  store: BuilderStore;
}

/**
 * Identity editor for the deployment and the *active* profile. With multi-
 * profile support, deployment-level fields (name, tenant) stay in this
 * section and live alongside the active profile's name + the per-config
 * default-profile selector (only surfaces when there are 2+ profiles —
 * single-profile configs have an obvious answer).
 */
export function ProfileBasicsEditor({ store }: ProfileBasicsEditorProps): JSX.Element {
  const profile = store.activeProfile;
  const showDefaultPicker = store.config.profiles.length > 1;

  return (
    <div className="editor">
      <header className="editor__head">
        <h2 className="editor__title">Profile basics</h2>
        <p className="editor__sub">
          Identity for this deployment. Tenant ID binds the config to a
          license — mismatch ⇒ runtime falls back to Core.{' '}
          <a href="https://mobilauncher.com/docs/customize#profile-basics" target="_blank" rel="noopener noreferrer" className="editor__doc-link">
            Read the docs →
          </a>
        </p>
      </header>

      <fieldset className="editor__group">
        <legend className="editor__group-label">Deployment</legend>
        <TextField
          label="Deployment name"
          hint="Shown in admin UIs; not visible on-device."
          value={store.config.deploymentName}
          onChange={(value) => store.patch({ deploymentName: value })}
        />
        <TextField
          label="Tenant ID"
          hint="UUID or org slug. Binds to license.tenantId."
          mono
          value={store.config.tenantId}
          onChange={(value) => store.patch({ tenantId: value })}
        />
        {showDefaultPicker && (
          <SelectField
            label="Default profile"
            hint="Profile the launcher runs at boot before any schedule matches."
            value={store.config.defaultProfileId}
            options={store.config.profiles.map((p) => ({ value: p.id, label: p.name }))}
            onChange={(id) => store.patch({ defaultProfileId: id })}
          />
        )}
      </fieldset>

      <fieldset className="editor__group">
        <legend className="editor__group-label">
          Active profile · <span className="editor__group-mono">{profile.name}</span>
        </legend>
        <TextField
          label="Profile name"
          hint="Shown in the launcher's top bar."
          value={profile.name}
          onChange={(value) => store.patchProfile({ name: value })}
        />
      </fieldset>
    </div>
  );
}
